import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import permissionsService from '../services/permissionsService';
import { ArrowLeft } from 'lucide-react';

const NewUser = () => {
  const navigate = useNavigate();
  const activeTenantName = localStorage.getItem('active_tenant_name') || '';
  const activeTenantId = localStorage.getItem('active_tenant_id') || '';
  const loggedUser = JSON.parse(localStorage.getItem('user') || 'null');
  const isPlatformAdmin = !!loggedUser?.is_platform_admin;
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    profile: 'Usuario',
    job_role: '',
    is_assignable: false,
    tenant_id: activeTenantId || '',
  });

  // Hierarquia do mais baixo ao mais alto.
  const JOB_ROLES = ['Estagiário', 'Assistente', 'Analista', 'Coordenador', 'Gerente', 'Diretor', 'C-Level'];
  const [profileOptions, setProfileOptions] = useState(['Usuario', 'Analista', 'Administrador']);

  React.useEffect(() => {
    permissionsService
      .getProfiles()
      .then((rows) => {
        const names = (Array.isArray(rows) ? rows : []).map((p) => p.name).filter(Boolean);
        if (names.length) setProfileOptions(names);
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!isPlatformAdmin) return;

    const loadTenants = async () => {
      try {
        const response = await api.get('/tenants/');
        setTenants(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching tenants:', error);
        setTenants([]);
      }
    };

    loadTenants();
  }, [isPlatformAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      alert('As senhas não coincidem!');
      return;
    }

    setLoading(true);

    try {
      const { confirm_password, ...userData } = formData;
      if (!isPlatformAdmin) {
        delete userData.tenant_id;
      } else {
        userData.tenant_id = userData.tenant_id ? Number(userData.tenant_id) : null;
      }
      await api.post('/users/', userData);
      navigate('/users');
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao criar usuário';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="new-user-page">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" aria-label="Voltar" onClick={() => navigate('/users')} data-testid="back-to-users-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="new-user-title">Novo Usuário</h1>
            <p className="text-gray-500">Cadastrar novo usuário no sistema</p>
            {activeTenantName && (
              <p className="text-sm text-blue-700 font-medium mt-1">Empresa ativa: {activeTenantName}</p>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações do Usuário</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome de Usuário *</label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="nome.usuario"
                    required
                    data-testid="user-username-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Usado para login</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="usuario@empresa.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="João"
                    required
                    data-testid="user-first-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sobrenome *</label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Silva"
                    required
                    data-testid="user-last-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Senha *</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                    data-testid="user-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Senha *</label>
                  <Input
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Digite a senha novamente"
                    minLength={6}
                    required
                    data-testid="user-confirm-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Perfil *</label>
                  <select
                    value={formData.profile}
                    onChange={(e) => {
                      const profile = e.target.value;
                      // Default sensato: end-user não entra na fila; analista/admin sim.
                      setFormData({ ...formData, profile, is_assignable: profile !== 'Usuario' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="user-profile-select"
                  >
                    {profileOptions.map((p) => (
                      <option key={p} value={p}>{p === 'Usuario' ? 'Usuário' : p}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Perfil de <strong>permissão</strong> (configure o que cada um vê em <strong>Permissões</strong>).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Função (cargo no time)</label>
                  <select
                    value={formData.job_role}
                    onChange={(e) => setFormData({ ...formData, job_role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Não definido —</option>
                    {JOB_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Usado nos relatórios (ex.: gestores não competem com analistas).</p>
                </div>

                <div className="flex items-center md:col-span-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!formData.is_assignable}
                      onChange={(e) => setFormData({ ...formData, is_assignable: e.target.checked })}
                      className="h-4 w-4 accent-blue-600"
                    />
                    Aparece na lista de <strong>Responsável / Fila</strong> dos chamados
                  </label>
                </div>

                {isPlatformAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Empresa *</label>
                    <select
                      value={formData.tenant_id}
                      onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Selecione a empresa</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Dica:</strong> O usuário receberá as credenciais por email (se configurado) e poderá alterar a senha no primeiro login.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/users')} data-testid="cancel-user-btn">
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-user-btn">
                  {loading ? 'Criando...' : 'Criar Usuário'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewUser;
